import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Button, TextField } from "@mui/material";
import {
  QrCodeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import ModernProductSelector from "./Selector/ModernProductSelector";
import { fetchProducts } from "../../api/product";
import toast from "react-hot-toast";
import QRCodeScanner from "./QRCodeScanner";
import ReportAddItemsDialog from "../dashboard/service/reports/report-add-items-dialog";

/**
 * Purchase Add Product Dialog Component
 * Based on item-consumption-list.js implementation
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Callback when dialog closes
 * @param {Function} onProductSelected - Callback when product is selected (product, formData) => void
 * @param {boolean} multiple - Allow multiple product selection
 * @param {string} title - Dialog title
 * @param {Function} renderAdditionalFields - Function to render additional fields (selectedProducts, formData, setFormData) => ReactNode
 * @param {Object} initialFormData - Initial form data for additional fields
 * @param {boolean} showBulkAdd - Show bulk add option
 * @param {Function} onBulkAdd - Callback for bulk add
 */
export const PurchaseAddProductDialog = ({
  open,
  onClose,
  onProductSelected,
  multiple = false,
  title = "Legg til produkt",
  renderAdditionalFields,
  initialFormData = {},
  showBulkAdd = false,
  onBulkAdd,
}) => {
  const [qrScanMode, setQrScanMode] = useState(false);
  const [manualSearchMode, setManualSearchMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [isSearching, setIsSearching] = useState(false);
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false);
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [productToAdd, setProductToAdd] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [mainDialogOpen, setMainDialogOpen] = useState(true);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setQrScanMode(false);
      setManualSearchMode(false);
      setSelectedProducts([]);
      setFormData(initialFormData);
      setBulkAddDialogOpen(false);
      setQuantityDialogOpen(false);
      setProductToAdd(null);
      setProductQuantity(1);
      setMainDialogOpen(true);
    }
  }, [open, initialFormData]);

  const handleQrCodeScan = async (scannedCode) => {
    if (!scannedCode || scannedCode.trim() === "") {
      toast.error("Skriv inn eller skann en QR-kode");
      return;
    }

    if (isSearching) return;

    setIsSearching(true);
    try {
      // First, try to search by product ID if it's a number
      let response = null;
      
      if (/^\d+$/.test(scannedCode.trim())) {
        try {
          response = await fetchProducts({
            ids: scannedCode.trim(),
            page: 1,
            per_page: 1,
            fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
          });
        } catch (error) {
          console.log("Product ID search failed, trying query search");
        }
      }
      
      // If ID search didn't work or code is not numeric, search by query
      if (!response || !response.data || response.data.length === 0) {
        response = await fetchProducts({
          query: scannedCode.trim(),
          page: 1,
          per_page: 10,
          fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
        });
      }
      
      if (response && response.data && response.data.length > 0) {
        const product = response.data[0];
        // For QR code scanning, show quantity dialog immediately
        setProductToAdd(product);
        setProductQuantity(1);
        setQuantityDialogOpen(true);
      } else {
        toast.error("Produkt ikke funnet. Prøv å søke manuelt.");
      }
    } catch (error) {
      console.error("Error searching product:", error);
      toast.error("Kunne ikke søke etter produkt");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddProductWithQuantity = async (product, quantity) => {
    if (!product) {
      toast.error("Ingen produkt valgt");
      return;
    }

    if (isAddingProduct) return;

    setIsAddingProduct(true);
    try {
      // Ensure we have full product object with all required fields
      let productToAdd = product;
      
      // If product doesn't have full data, fetch it
      if (!product.title && !product.displayTitle) {
        try {
          const response = await fetchProducts({
            ids: product.id.toString(),
            page: 1,
            per_page: 1,
            fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
          });
          
          if (response?.data && response.data.length > 0) {
            productToAdd = response.data[0];
          }
        } catch (error) {
          console.error("Error fetching product details:", error);
        }
      }

      // Prepare form data with quantity from dialog or formData
      const productFormData = {
        ...formData,
        quantity: parseFloat(quantity) || formData.quantity || 1,
      };

      // Call onProductSelected with product and form data
      await onProductSelected(productToAdd, productFormData);
      
      // Close dialogs and reset state
      setQuantityDialogOpen(false);
      setProductToAdd(null);
      setProductQuantity(1);
      setQrScanMode(false);
      onClose();
      
      toast.success("Produkt lagt til");
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error("Kunne ikke legge til produkt");
    } finally {
      setIsAddingProduct(false);
    }
  };

  const handleAddProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Velg minst ett produkt");
      return;
    }

    try {
      if (multiple) {
        await onProductSelected(selectedProducts, formData);
      } else {
        await onProductSelected(selectedProducts[0], formData);
      }
      
      // Close dialog on success
      setManualSearchMode(false);
      setSelectedProducts([]);
      setFormData(initialFormData);
      onClose();
    } catch (error) {
      console.error("Error adding products:", error);
      // Error handling is done by onProductSelected
    }
  };

  return (
    <>
      {/* Main Add Product Dialog */}
      <Transition.Root show={open && !quantityDialogOpen && mainDialogOpen && !bulkAddDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    {title}
                  </Dialog.Title>

                  {!qrScanMode && !manualSearchMode && (
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => setQrScanMode(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <QrCodeIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">Skann QR-kode</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setManualSearchMode(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <MagnifyingGlassIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">Søk manuelt</span>
                      </button>
                      {showBulkAdd && (
                        <button
                          type="button"
                          onClick={() => {
                            setBulkAddDialogOpen(true);
                            setMainDialogOpen(false);
                          }}
                          className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <PlusIcon className="h-5 w-5 mr-3 text-gray-600" />
                          <span className="text-gray-700 font-medium">Legg til produkter i bulk</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* QR Code Scan Mode */}
                  {qrScanMode && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setQrScanMode(false);
                          }}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          ← Tilbake
                        </button>
                      </div>
                      <QRCodeScanner
                        open={qrScanMode}
                        onScan={handleQrCodeScan}
                        onBack={() => setQrScanMode(false)}
                        placeholder="Skriv produktnummer eller skann QR-kode"
                      />
                    </div>
                  )}

                  {/* Manual Search Mode */}
                  {manualSearchMode && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setManualSearchMode(false);
                            setSelectedProducts([]);
                          }}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          ← Tilbake
                        </button>
                      </div>
                      <ModernProductSelector
                        open={true}
                        selectedProducts={selectedProducts}
                        onChange={(products) => {
                          setSelectedProducts(products);
                        }}
                        onClose={() => {
                          setManualSearchMode(false);
                        }}
                        multiple={multiple}
                        filters={[]}
                        showHeader={false}
                      />
                      {selectedProducts.length > 0 && (
                        <div className="mt-4 space-y-4">
                          {renderAdditionalFields && renderAdditionalFields(selectedProducts, formData, setFormData)}
                          <div className="flex space-x-2">
                            <Button
                              variant="contained"
                              onClick={handleAddProducts}
                              className="flex-1"
                              disabled={isAddingProduct}
                            >
                              {isAddingProduct ? "Legger til..." : `Legg til ${multiple ? `(${selectedProducts.length})` : ""}`}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setSelectedProducts([]);
                                setFormData(initialFormData);
                              }}
                            >
                              Nullstill
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!qrScanMode && !manualSearchMode && (
                    <div className="mt-4 flex justify-end">
                      <Button variant="outlined" onClick={onClose}>
                        Lukk
                      </Button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Quantity Dialog for QR Code Scanned Products */}
      <Transition.Root show={quantityDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => {
          setQuantityDialogOpen(false);
          setProductToAdd(null);
          setProductQuantity(1);
        }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                  <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                    Legg til produkt
                  </Dialog.Title>

                  {productToAdd && (
                    <div className="space-y-4">
                      <div>
                        <div className="text-sm font-medium text-gray-700 mb-1">
                          Produkt:
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="font-medium text-gray-900">
                            {productToAdd.displayTitle || productToAdd.title || `Produkt ${productToAdd.id}`}
                          </div>
                          {productToAdd.item_number && (
                            <div className="text-sm text-gray-500 mt-1">
                              Art.nr: {productToAdd.item_number}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Antall *
                        </label>
                        <TextField
                          fullWidth
                          type="number"
                          variant="outlined"
                          value={productQuantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || (!isNaN(value) && parseFloat(value) >= 0)) {
                              setProductQuantity(value);
                            }
                          }}
                          inputProps={{
                            step: "0.01",
                            min: "0.01",
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "0.5rem",
                            },
                          }}
                          autoFocus
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              if (productQuantity && parseFloat(productQuantity) > 0) {
                                handleAddProductWithQuantity(productToAdd, productQuantity);
                              }
                            }
                          }}
                        />
                        {productToAdd.unit?.title && (
                          <div className="text-sm text-gray-500 mt-1">
                            Enhet: {productToAdd.unit.title}
                          </div>
                        )}
                      </div>

                      {renderAdditionalFields && renderAdditionalFields([productToAdd], formData, setFormData)}

                      <div className="flex justify-end space-x-2 pt-4">
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setQuantityDialogOpen(false);
                            setProductToAdd(null);
                            setProductQuantity(1);
                          }}
                        >
                          Avbryt
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => {
                            if (productQuantity && parseFloat(productQuantity) > 0) {
                              handleAddProductWithQuantity(productToAdd, productQuantity);
                            } else {
                              toast.error("Antall må være større enn 0");
                            }
                          }}
                          disabled={!productQuantity || parseFloat(productQuantity) <= 0 || isAddingProduct}
                        >
                          {isAddingProduct ? "Legger til..." : "Legg til"}
                        </Button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Bulk Add Items Dialog */}
      {showBulkAdd && (
        <ReportAddItemsDialog
          open={bulkAddDialogOpen}
          onClose={() => {
            setBulkAddDialogOpen(false);
            setMainDialogOpen(true);
            onClose();
          }}
          onSave={(itemsToAdd) => {
            if (onBulkAdd) {
              onBulkAdd(itemsToAdd);
            }
            setBulkAddDialogOpen(false);
            setMainDialogOpen(true);
            onClose();
          }}
          report={null}
          onSubDialogStateChange={() => {}}
        />
      )}
    </>
  );
};

